import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LoadingButton from './LoadingButton';
import LimitRequestStatusChip from './LimitRequestStatusChip';
import { apiService } from '../services/api';
import { ResellerLimitRequest } from '../types/reseller';
import { formatCredits } from '../utils/credits';

// BANDEJA del admin: solicitudes de AMPLIACIÓN DEL TECHO mayorista.
//
// Qué se aprueba aquí, porque es el punto donde se confunde todo el mundo: el
// partner COMPRA créditos él mismo, sin pedir permiso, mientras el saldo
// resultante quepa en su techo. El techo es su LÍNEA DE CRÉDITO — la exposición
// que el marketplace le acepta antes de facturar. Aprobar una solicitud NO le
// regala créditos ni mueve su saldo: le SUBE el límite hasta el que podrá
// comprar. Por eso todas las cifras de esta bandeja son techos, no compras.
//
// Vive como componente y no dentro de la página para poder colgarla del listado
// de partners (bandeja global) y, si hace falta, de la ficha de UN partner
// (`resellerId`) sin duplicar la lógica de decisión.

interface Props {
  // Sin él, la bandeja es GLOBAL (todos los partners), que es como se trabaja
  // una bandeja. Con él, solo las de ese partner.
  resellerId?: number;
  // Aviso al contenedor de que el techo de alguien ha cambiado (para repintar
  // su listado sin recargar la página).
  onDecided?: () => void;
}

const LimitRequestsInbox: React.FC<Props> = ({ resellerId, onDecided }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<ResellerLimitRequest[]>([]);
  const [loading, setLoading] = useState(true);
  // Solo lo pendiente por defecto: una bandeja enseña trabajo, no historia.
  const [onlyPending, setOnlyPending] = useState(true);
  const [target, setTarget] = useState<{ row: ResellerLimitRequest; approve: boolean } | null>(null);
  const [grantCredits, setGrantCredits] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRequests(await apiService.getResellerLimitRequests(
        onlyPending ? 'pending' : undefined, resellerId));
    } catch (err) {
      console.error('Failed to load limit requests:', err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [onlyPending, resellerId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDecision = (row: ResellerLimitRequest, approve: boolean) => {
    setTarget({ row, approve });
    // Se precarga lo PEDIDO: aprobar tal cual es el caso normal, y conceder
    // menos es una edición consciente sobre una cifra ya escrita.
    setGrantCredits(approve ? String(Number(row.requested_max_credits)) : '');
    setNote('');
    setError('');
  };

  const handleDecide = async () => {
    if (!target) return;
    const amount = Number(grantCredits);
    if (target.approve && (Number.isNaN(amount) || amount <= 0)) return;
    try {
      setSaving(true);
      setError('');
      const res = target.approve
        ? await apiService.approveResellerLimitRequest(target.row.id, {
            max_credits: amount,
            note: note.trim() || undefined
          })
        : await apiService.rejectResellerLimitRequest(target.row.id, {
            note: note.trim() || undefined
          });
      // `email_sent` es fail-soft: solo se anuncia el aviso si de verdad salió.
      toast.success(
        target.approve
          ? (res.email_sent
              ? t('limitRequests.approvedNotified', 'Techo ampliado y partner avisado por correo')
              : t('limitRequests.approved', 'Techo ampliado'))
          : (res.email_sent
              ? t('limitRequests.rejectedNotified', 'Solicitud rechazada y partner avisado por correo')
              : t('limitRequests.rejected', 'Solicitud rechazada'))
      );
      setTarget(null);
      load();
      onDecided?.();
    } catch (err: any) {
      console.error('Failed to decide limit request:', err);
      setError(err?.detail || t('limitRequests.error', 'No se pudo resolver la solicitud'));
    } finally {
      setSaving(false);
    }
  };

  const formatMoment = (raw: string): string => {
    const utc = raw.endsWith('Z') ? raw : `${raw}Z`;
    const d = new Date(utc);
    return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  // Bandeja vacía y filtrada a pendientes: no se pinta nada. Un bloque «no hay
  // solicitudes» permanente es ruido en una pantalla que ya tiene su tabla.
  if (!loading && requests.length === 0 && onlyPending) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6">
              {t('limitRequests.title', 'Solicitudes de ampliación de techo')}
            </Typography>
            {pendingCount > 0 && (
              <Typography variant="body2" color="warning.main" fontWeight={700}>
                {t('limitRequests.pendingCount', '{{count}} pendientes', { count: pendingCount })}
              </Typography>
            )}
          </Box>
          <Button size="small" onClick={() => setOnlyPending((v) => !v)} sx={{ textTransform: 'none' }}>
            {onlyPending
              ? t('limitRequests.showAll', 'Ver también las resueltas')
              : t('limitRequests.showPending', 'Ver solo pendientes')}
          </Button>
        </Box>
        {/* Qué significa aprobar: no se regalan créditos, se sube el límite. */}
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('limitRequests.help', 'El partner compra créditos él mismo mientras quepan en su techo. Aprobar aquí no le da créditos: le sube el límite hasta el que puede comprar.')}
        </Typography>

        {loading ? (
          <Typography color="text.secondary" align="center" py={2}>
            {t('common.loading', 'Cargando...')}
          </Typography>
        ) : requests.length === 0 ? (
          <Typography color="text.secondary" align="center" py={2}>
            {t('limitRequests.empty', 'No hay solicitudes.')}
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('limitRequests.fields.date', 'Fecha')}</TableCell>
                  {resellerId === undefined && (
                    <TableCell>{t('limitRequests.fields.partner', 'Partner')}</TableCell>
                  )}
                  <TableCell align="right">{t('limitRequests.fields.current', 'Techo actual')}</TableCell>
                  <TableCell align="right">{t('limitRequests.fields.requested', 'Techo pedido')}</TableCell>
                  <TableCell>{t('limitRequests.fields.note', 'Motivo')}</TableCell>
                  <TableCell>{t('limitRequests.fields.status', 'Estado')}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatMoment(row.created_at)}</TableCell>
                    {resellerId === undefined && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.reseller_name || `#${row.reseller_id}`}
                        </Typography>
                        {row.requested_by_email && (
                          <Typography variant="caption" color="text.secondary">
                            {row.requested_by_email}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      {row.current_max_credits === null || row.current_max_credits === undefined
                        ? '—'
                        : formatCredits(row.current_max_credits)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700}>
                        {formatCredits(row.requested_max_credits)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="caption" color="text.secondary">
                        {row.note || row.decision_note || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><LimitRequestStatusChip status={row.status} /></TableCell>
                    <TableCell align="right">
                      {row.status === 'pending' && (
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => openDecision(row, true)}
                            sx={{ textTransform: 'none' }}
                          >
                            {t('limitRequests.approve', 'Aprobar')}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => openDecision(row, false)}
                            sx={{ textTransform: 'none' }}
                          >
                            {t('limitRequests.reject', 'Rechazar')}
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Decisión. Aprobar permite conceder un techo distinto del pedido: sin
          eso, la negociación se iría al correo — que es lo que este flujo quita. */}
      <Dialog open={!!target} onClose={() => !saving && setTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {target?.approve
            ? t('limitRequests.approveTitle', 'Ampliar el techo')
            : t('limitRequests.rejectTitle', 'Rechazar la solicitud')}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {target && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{target.row.reseller_name || `#${target.row.reseller_id}`}</AlertTitle>
              {t('limitRequests.summary', 'Tiene {{current}} y pide {{requested}} créditos de techo.', {
                current: formatCredits(target.row.current_max_credits ?? 0),
                requested: formatCredits(target.row.requested_max_credits)
              })}
            </Alert>
          )}
          {target?.approve && (
            <TextField
              autoFocus
              margin="dense"
              type="number"
              fullWidth
              label={t('limitRequests.grant', 'Techo que concedes (créditos)') + ' *'}
              value={grantCredits}
              onChange={(e) => setGrantCredits(e.target.value)}
              inputProps={{ min: 1, step: '1' }}
              helperText={t('limitRequests.grantHelp', 'Puedes conceder menos de lo pedido: es el techo con el que se queda.')}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            label={t('limitRequests.note', 'Mensaje para el partner')}
            helperText={t('limitRequests.noteHelp', 'Va en el correo que recibe. En un rechazo es lo más importante del mensaje.')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)} disabled={saving}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleDecide}
            loading={saving}
            color={target?.approve ? 'primary' : 'error'}
            variant="contained"
            disabled={target?.approve && (grantCredits.trim() === '' || Number(grantCredits) <= 0)}
          >
            {target?.approve
              ? t('limitRequests.approve', 'Aprobar')
              : t('limitRequests.reject', 'Rechazar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default LimitRequestsInbox;
